import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Activity, FileText, Trash2, RefreshCw, Clock, User } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RecentActivityWidget = ({ limit = 10 }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivity();
    const interval = setInterval(fetchActivity, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchActivity = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/activity-recent?limit=${limit}`, { withCredentials: true });
      setActivities(response.data);
    } catch (error) {
      console.error('Error fetching activity:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'consent': return <FileText className="w-4 h-4" />;
      case 'rtbf': return <Trash2 className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getActivityBadge = (activity) => {
    if (activity.type === 'consent') {
      const statusColors = {
        active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        revoked: 'bg-red-50 text-red-700 border-red-200',
        expired: 'bg-amber-50 text-amber-700 border-amber-200'
      };
      return (
        <Badge className={statusColors[activity.status] || 'bg-slate-50 text-slate-700'}>
          {activity.action === 'created' ? 'Novo' : activity.status}
        </Badge>
      );
    }
    
    if (activity.type === 'rtbf') {
      const statusColors = {
        pending: 'bg-amber-50 text-amber-700 border-amber-200',
        approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        completed: 'bg-blue-50 text-blue-700 border-blue-200',
        rejected: 'bg-red-50 text-red-700 border-red-200'
      };
      return (
        <Badge className={statusColors[activity.action] || 'bg-slate-50 text-slate-700'}>
          RTBF - {activity.action}
        </Badge>
      );
    }
    
    return null;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return 'Agora';
    if (diffMins < 60) return `Há ${diffMins} min`;
    if (diffHours < 24) return `Há ${diffHours}h`;
    return date.toLocaleDateString('pt-PT');
  };

  return (
    <Card className="border-slate-200" data-testid="recent-activity-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Activity className="w-5 h-5 text-blue-600" />
            Atividade Recente
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={fetchActivity} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            Sem atividade recente
          </div>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {activities.map((activity, index) => (
              <div 
                key={index} 
                className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <div className={`p-2 rounded-full ${
                  activity.type === 'consent' ? 'bg-blue-100 text-blue-600' : 'bg-red-100 text-red-600'
                }`}>
                  {getActivityIcon(activity.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {getActivityBadge(activity)}
                    <span className="text-sm font-medium text-[#0F172A] truncate">
                      {activity.type === 'consent' ? activity.contact_type : 'Pedido RTBF'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                    <User className="w-3 h-3" />
                    <span className="truncate">{activity.user_name || activity.user_email}</span>
                  </div>
                  <div className="flex items-center gap-1 mt-1 text-xs text-slate-400">
                    <Clock className="w-3 h-3" />
                    {formatTimestamp(activity.timestamp)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentActivityWidget;
